import {
  HOME_WIDGET_SIZES,
  type HomeWidgetData,
  type HomeWidgetDefinition,
  type HomeWidgetKind,
  type HomeWidgetSettings,
  type HomeWidgetState,
} from './homeWidgetData';
export const HOME_WIDGET_TYPE_TITLES: Record<HomeWidgetKind, string> = {
  stat: 'Число с изменением',
  'status-list': 'Объекты со статусами',
  chart: 'Графики',
  'mini-table': 'Мини-таблица',
  'event-feed': 'Лента событий',
  cells: 'Клетки с состоянием',
  'week-grid': 'Неделя',
  'quick-action': 'Быстрое действие',
  note: 'Заметка и ссылки',
};
const work = { id: 'studio', title: 'Мастерская', icon: 'tasks' as const },
  finance = { id: 'ledger', title: 'План средств', icon: 'finance' as const },
  infra = { id: 'garden', title: 'Учебный парк', icon: 'box' as const },
  calendar = { id: 'team', title: 'Команда', icon: 'calendar' as const },
  personal = { id: 'personal', title: 'Личное', icon: 'notes' as const };
const definition = (
  id: string,
  type: HomeWidgetKind,
  title: string,
  application: HomeWidgetDefinition['application'],
  settings?: HomeWidgetDefinition['settings'],
  sizes = HOME_WIDGET_SIZES[type],
): HomeWidgetDefinition => ({
  id,
  type,
  title,
  application,
  settings,
  allowedSizes: sizes,
  showOpenApplication: type !== 'stat' && type !== 'quick-action',
  ...(type === 'chart'
    ? { chartVariant: id === 'load-chart' ? 'line' : id === 'density-chart' ? 'heatmap' : 'bar' }
    : {}),
});
export const HOME_WIDGET_CATALOG: readonly HomeWidgetDefinition[] = [
  definition('active-count', 'stat', 'Заявки в работе', work, { onlyMine: true, period: true }),
  definition('work-queue', 'status-list', 'Очередь проверок', work, { onlyMine: true, period: true }),
  definition(
    'income-chart',
    'chart',
    'Поступления и расходы',
    finance,
    { period: true },
    HOME_WIDGET_SIZES.chart.filter((size) => size.rows >= 3),
  ),
  definition(
    'load-chart',
    'chart',
    'Ритм нагрузки',
    infra,
    { period: true },
    HOME_WIDGET_SIZES.chart.filter((size) => size.rows <= 3),
  ),
  definition('density-chart', 'chart', 'Дни обращений', work, { onlyMine: true, period: true }),
  definition('plan-table', 'mini-table', 'План по направлениям', finance, { period: true }),
  definition('recent-events', 'event-feed', 'Последние изменения', work, { onlyMine: true }),
  definition('resource-cells', 'cells', 'Ресурсы площадки', infra),
  definition('team-week', 'week-grid', 'Команда на неделе', calendar, { onlyMine: true }),
  definition('new-request', 'quick-action', 'Новая заявка', work),
  definition('personal-note', 'note', 'На заметку', personal),
];
export const DEFAULT_HOME_SETTINGS: HomeWidgetSettings = { onlyMine: false, period: 'week' };
export function sampleHomeWidgetData(id: string, settings: HomeWidgetSettings, now: number): HomeWidgetData {
  const titles = [
    'Сверка доступа',
    'Материалы к встрече',
    'Осмотр мастерской',
    'Проверка плана',
    'Сезонная подготовка',
    'Памятка команды',
  ];
  const count = settings.onlyMine ? 3 : 8;
  switch (id) {
    case 'active-count':
      return {
        type: 'stat',
        label: settings.onlyMine ? 'Мои заявки' : 'Активные заявки',
        value: settings.onlyMine ? '12' : '24',
        delta: {
          value: settings.onlyMine ? -2 : -6,
          label:
            'за ' + (settings.period === 'day' ? 'день' : settings.period === 'week' ? 'неделю' : 'месяц'),
          goodDirection: 'down',
        },
        trend: [18, 21, 20, 16, 17, 14, 12],
      };
    case 'work-queue':
      return {
        type: 'status-list',
        total: count + 4,
        items: titles.slice(0, count).map((title, index) => ({
          id: 'sample-task-' + index,
          title,
          status: index === 1 ? { label: 'Нужен ответ', tone: 'warn' } : { label: 'В работе', tone: 'info' },
          detail: index % 2 ? 'Команда мастерской' : 'Лея Орлова',
        })),
      };
    case 'income-chart':
      return {
        type: 'chart',
        variant: 'bar',
        categories: [
          { key: 'aug', label: 'Авг' },
          { key: 'sep', label: 'Сен' },
          { key: 'oct', label: 'Окт' },
        ],
        series: [
          { id: 'in', label: 'Поступления', color: 2, values: [120, 160, 145] },
          { id: 'out', label: 'Расходы', color: 4, values: [90, 110, 100] },
        ],
      };
    case 'load-chart':
      return {
        type: 'chart',
        variant: 'line',
        unit: '%',
        series: [
          {
            id: 'load',
            label: 'Нагрузка',
            color: 2,
            points: [34, 38, 29, 44, 42, 48, 36].map((y, index) => ({ x: now - (6 - index) * 3600000, y })),
          },
        ],
      };
    case 'density-chart':
      return {
        type: 'chart',
        variant: 'heatmap',
        from: '2026-10-01',
        to: '2026-10-28',
        domainMax: 8,
        values: Object.fromEntries(
          Array.from({ length: 28 }, (_, index) => [
            '2026-10-' + String(index + 1).padStart(2, '0'),
            settings.onlyMine ? index % 4 : index % 9,
          ]),
        ),
      };
    case 'plan-table':
      return {
        type: 'mini-table',
        columns: [
          { id: 'title', label: 'Направление' },
          { id: 'plan', label: 'План', numeric: true },
          { id: 'fact', label: 'Факт', numeric: true },
        ],
        total: 7,
        rows: ['Подготовка', 'Материалы', 'Обслуживание', 'Обучение', 'Доставка'].map((title, index) => ({
          id: 'plan-' + index,
          cells: { title, plan: [120, 85, 100, 60, 40][index], fact: [110, 85, 92, 54, 0][index] },
        })),
      };
    case 'recent-events':
      return {
        type: 'event-feed',
        total: 12,
        items: titles.slice(0, settings.onlyMine ? 2 : 5).map((title, index) => ({
          id: 'event-' + index,
          title: title + ' · обновлено',
          at: new Date(now - (index + 1) * 600000).toISOString(),
          actor: index % 2 ? 'Марк Лесной' : 'Лея Орлова',
          kind: index % 2 ? 'change' : 'resolve',
        })),
      };
    case 'resource-cells':
      return {
        type: 'cells',
        total: 40,
        items: Array.from({ length: 32 }, (_, index) => ({
          id: 'resource-' + index,
          label: 'Учебный ресурс ' + (index + 1),
          status: index === 7 ? 'warning' : index === 18 ? 'error' : index === 25 ? 'unknown' : 'ok',
        })),
      };
    case 'team-week':
      return {
        type: 'week-grid',
        anchor: '2026-10-26',
        timeZone: 'Europe/Moscow',
        rows: [
          { id: 'lea', title: 'Лея Орлова' },
          ...(settings.onlyMine ? [] : [{ id: 'mark', title: 'Марк Лесной' }]),
        ],
        shifts: [
          {
            id: 'shift-1',
            rowId: 'lea',
            title: 'Подготовка',
            start: '2026-10-26T09:00:00+03:00',
            end: '2026-10-27T15:00:00+03:00',
            tone: 'info',
          },
          ...(!settings.onlyMine
            ? [
                {
                  id: 'shift-2',
                  rowId: 'mark',
                  title: 'Проверка',
                  start: '2026-10-28T10:00:00+03:00',
                  end: '2026-10-30T16:00:00+03:00',
                  tone: 'success' as const,
                },
              ]
            : []),
        ],
      };
    case 'new-request':
      return {
        type: 'quick-action',
        label: 'Создание учебной заявки',
        buttonLabel: 'Создать',
        inputLabel: 'Краткое описание',
        placeholder: 'Кратко о задаче',
      };
    default:
      return {
        type: 'note',
        text: 'Подготовить материалы к встрече.\nСверить план следующей недели.',
        links: [
          { id: 'objects', label: 'Мои объекты', href: '/design/objects' },
          { id: 'progress', label: 'Ход проверки', href: '/design/progress' },
        ],
      };
  }
}
export function sampleHomeWidgetState(
  definition: HomeWidgetDefinition,
  settings: HomeWidgetSettings,
  now: number,
): HomeWidgetState {
  return {
    state: 'ready',
    data: sampleHomeWidgetData(definition.id, settings, now),
    updatedAt: new Date(now - 300000).toISOString(),
    ...(definition.id === 'work-queue' ? { attention: { label: 'Нужен ответ', tone: 'warn' as const } } : {}),
  };
}
